<template>
  <ion-page>
    <ion-content>
      <div class="banner-container">
        <BannerComponent
          :src="isMobile ? mobileBannerSrc : desktopBannerSrc"
          title="MESSAGES"
          description="All the messages your team has sent, across every request."
        />
      </div>
      <ion-grid :fixed="true" class="ion-padding">
        <h2 class="ion-padding-bottom">Messages</h2>

        <MessageList
          :messages="allMessages"
          :is-loading="isLoading"
          :has-loaded="hasLoaded"
          :new-message-ids="newMessageIds"
          empty-text="You don't have any messages yet."
          @upload-photo="prepareModal"
        />
      </ion-grid>
      <FooterComponent />
    </ion-content>
    <AdditionalPhotoUploadModalComponent
      v-if="selectedClaimId"
      ref="photoModal"
      :claimId="selectedClaimId"
    />
  </ion-page>
</template>

<script setup>
import { IonPage, IonContent, IonGrid, onIonViewWillEnter, onIonViewWillLeave } from '@ionic/vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useCustomerStore } from '../store/customer';
import { useBrandingStore } from '../store/branding';
import { useUnreadMessages } from '@/composables/useUnreadMessages';
import BannerComponent from '@/components/BannerComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import MessageList from '@/components/MessageList.vue';
import AdditionalPhotoUploadModalComponent from '@/components/AdditionalPhotoUploadModalComponent.vue';

const customerStore = useCustomerStore();
const brandingStore = useBrandingStore();
const { isLoading, hasLoaded, newMessageIds, load, stopTracking } = useUnreadMessages();

const selectedClaimId = ref(null);
const photoModal = ref(null);

const desktopBannerSrc = ref(brandingStore.brand.desktop_banner_6);
const mobileBannerSrc = ref(brandingStore.brand.mobile_banner_6);
const screenWidth = ref(window.innerWidth);
const isMobile = computed(() => screenWidth.value < 768);
const handleResize = () => {
  screenWidth.value = window.innerWidth;
};

// Claims already carry .product and .claim_no from associateClaimsAndPolicies, so each message
// just needs pairing with its claim for the label - no separate policy lookup required.
const allMessages = computed(() => {
  const messages = [];
  for (const claim of customerStore.claims) {
    for (const message of customerStore.messagesByClaimId[claim.id] || []) {
      messages.push({ ...message, claim });
    }
  }
  return messages.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
});

function prepareModal(id) {
  selectedClaimId.value = Number(id);
  // Use a timeout to ensure the DOM has updated before opening the modal - matches
  // RequestsPage.vue's own use of this component.
  setTimeout(() => {
    if (photoModal.value && photoModal.value.modal) {
      photoModal.value.modal.$el.present();
    }
  }, 0);
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

async function fetchAllMessages() {
  await customerStore.fetchClaims();
  const results = await Promise.all(
    customerStore.claims.map((claim) => customerStore.fetchMessagesForClaim(claim.id))
  );
  // Only a total failure shows the error state - one bad claim among several good ones just
  // quietly omits that claim's messages (same as this always silently did before hasLoaded
  // existed), rather than blanking the whole inbox over a single claim's fetch.
  return customerStore.claims.length === 0 || results.some(Boolean);
}

// ion-router-outlet keeps this page's component instance alive across navigations, so a plain
// onMounted would only ever fire once per app session - see useUnreadMessages.js for why
// onIonViewWillEnter/onIonViewWillLeave are used instead.
onIonViewWillEnter(() => load(fetchAllMessages, () => allMessages.value));
onIonViewWillLeave(() => stopTracking());
</script>

<style lang="scss" scoped>
// Clears the fixed nav header in NavigationMenu.vue - same rule every other page defines.
.ion-page {
  margin-top: 125px;
}

@media only screen and (max-width: 768px) {
  .ion-page {
    margin-top: 42px;
  }
}

.banner-container {
  position: relative;
}
</style>
